/*
 * InterviewHome — the Interviews workspace's starting point (#/interviews) and
 * its history view (#/interviews/history).
 *
 *   AI Interview / Written Interview → the setup flow
 *   Recently practiced, Quick actions, "Not sure where to start?"
 *
 * Recommendations read real data only: the learner's goal, their memory tree
 * (weak, practised topics) and the role catalogue's hub scope.
 */
import React, { useEffect, useMemo, useState } from 'react';
import APIService from '../../services/APIService';
import IvShell from './InterviewChrome';
import { loadGoal } from './HomeDashboard';
import { indexTree } from './paths';
import {
  ROLES, ago, historyFilterOf, recommendFocus, roleForGoal, scoreText, takeawayOf, typeLabel,
} from './interviewData';
import InterviewAvatar from './InterviewAvatar';
import { loadPrefs } from './settingsPrefs';
import {
  IconArrowRight, IconCheck, IconInterview, IconList, IconMic, IconTarget,
} from './icons';

const scoreCls = (s) => (s == null ? '' : s >= 80 ? 'good' : s >= 60 ? 'ok' : 'low');
const Score = ({ s }) => <b className={`ivx-score ${scoreCls(s)}`} title="Average of the question scores, each 0–100">{scoreText(s)}</b>;

/*
 * Recommend an interview from real signals: the learner's goal (else the role
 * they practise most), then the focus areas recommendFocus picks for that role
 * — each with its reason (last interview, accuracy, due reviews, recency).
 */
const recommend = ({ memTree, hub, due, history }) => {
  const byMem = indexTree(memTree || []);
  const live = ROLES.filter((r) => r.rootIds.length);
  let role = roleForGoal(loadGoal());
  let because = role ? `your goal (${role.title})` : '';
  if (!role) {
    const weight = (r) => r.rootIds.reduce((n, id) => n + (byMem[id]?.attempts || 0), 0);
    role = [...live].sort((a, b) => weight(b) - weight(a))[0];
    because = weight(role) ? `what you practise most (${role.title})` : '';
  }
  const focus = recommendFocus({ role, hub, memTree, due, history, max: 3 });
  const setup = { roleId: role.id, type: 'video', focus: focus.map(({ id, name, reason }) => ({ id, name, reason })), recommended: focus.length === 0 };
  const weakCount = focus.filter((f) => /^Weak area|Flagged/.test(f.reason)).length;
  return { setup, role, focus, weakCount, because: because || `a core ${role.title} interview` };
};

/* ── history table (shared by Home's "Recent Interviews" and the full view) ── */
const HistoryRows = ({ rows, onView, compact }) => (
  <table className="pu-table ivx-table">
    <thead>
      <tr>
        <th>Interview</th><th>Type</th>{!compact && <th>Level</th>}<th>Date</th>
        <th><abbr title="Average of the question scores, each 0–100">Score</abbr></th><th>Key takeaway</th><th><span className="pu-sr">Action</span></th>
      </tr>
    </thead>
    <tbody>
      {rows.map((e) => (
        <tr key={e.id}>
          <td><b>{e.title}</b><small>{e.setup?.company || (e.kind === 'library' ? 'Interview library' : 'Role-based')}</small></td>
          <td><span className={`ivx-type t-${historyFilterOf(e.kind)}`}>{typeLabel(e.kind)}</span></td>
          {!compact && <td className="ivx-muted">{e.setup?.level || '—'}</td>}
          <td className="ivx-muted">{ago(e.ts)}</td>
          <td><Score s={e.score} /></td>
          <td className="ivx-takeaway">{e.takeaway || takeawayOf(e.report)}</td>
          <td className="ivx-act">{e.report ? <button type="button" className="pu-link" onClick={() => onView(e)} aria-label={`View feedback for ${e.title}`}>View <IconArrowRight size={12} /></button> : <span className="ivx-muted">—</span>}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const InterviewHome = ({
  view = 'home', history, onSetup, onOpenReport, onOpenHistory, onBack, onOpenLibrary, hub, ...shell
}) => {
  const [rec, setRec] = useState(null);       // null | 'loading' | {setup, role, weak, reason} | {error}
  const [filter, setFilter] = useState('all');
  const [roleF, setRoleF] = useState('');
  const [companyF, setCompanyF] = useState('');
  const [dateF, setDateF] = useState('any');
  const [scoreF, setScoreF] = useState('any');
  // homepage Recent Interviews filters (separate from the full history view)
  const [tab, setTab] = useState('all');
  const [hRole, setHRole] = useState('');
  const [hCompany, setHCompany] = useState('');
  const [hSort, setHSort] = useState('recent');

  const getRec = async () => {
    setRec('loading');
    try {
      const [mem, snap] = await Promise.all([APIService.getMemoryTree().catch(() => ({})), APIService.getEngineMemory().catch(() => ({}))]);
      setRec(recommend({ memTree: mem.tree || [], hub, due: snap?.due_reviews || [], history }));
    } catch {
      setRec(recommend({ memTree: [], hub, due: [], history }));
    }
  };

  const rows = useMemo(() => history.filter((e) => {
    if (filter !== 'all' && historyFilterOf(e.kind) !== filter) return false;
    if (roleF && e.title !== roleF) return false;
    if (companyF && (e.setup?.company || '') !== companyF) return false;
    const days = (Date.now() - Date.parse(e.ts)) / 86400000;
    if (dateF === '7' && days > 7) return false;
    if (dateF === '30' && days > 30) return false;
    if (scoreF === 'high' && !(e.score >= 80)) return false;
    if (scoreF === 'mid' && !(e.score >= 60 && e.score < 80)) return false;
    if (scoreF === 'low' && !(e.score != null && e.score < 60)) return false;
    return true;
  }).sort((a, b) => (Date.parse(b.ts) || 0) - (Date.parse(a.ts) || 0)), [history, filter, roleF, companyF, dateF, scoreF]);
  const roleNames = [...new Set(history.map((e) => e.title))];
  const companies = [...new Set(history.map((e) => e.setup?.company).filter(Boolean))];

  // The recommendation loads on its own once the hub is ready (refreshable).
  const recStarted = React.useRef(false);
  useEffect(() => {
    if (!hub || view !== 'home' || recStarted.current) return;
    recStarted.current = true;
    getRec();
  }, [hub, view]);   // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Recent Interviews: tab → role/company → sort, then the empty state ── */
  const base = useMemo(() => history.filter((e) => e.kind !== 'rapidfire'), [history]);   // rapid fire lives in Practice
  const counts = { all: base.length, ai: base.filter((e) => historyFilterOf(e.kind) === 'ai').length, written: base.filter((e) => historyFilterOf(e.kind) === 'written').length };
  const inTab = useMemo(() => base.filter((e) => tab === 'all' || historyFilterOf(e.kind) === tab), [base, tab]);
  const tabRoles = [...new Set(inTab.map((e) => e.title))];
  const tabCompanies = [...new Set(inTab.map((e) => e.setup?.company).filter(Boolean))];
  const role = tabRoles.includes(hRole) ? hRole : '';            // a filter that no longer applies is dropped
  const company = tabCompanies.includes(hCompany) ? hCompany : '';
  const shown = useMemo(() => {
    const list = inTab.filter((e) => (!role || e.title === role) && (!company || e.setup?.company === company));
    const t = (e) => Date.parse(e.ts) || 0;
    const sorters = {
      recent: (a, b) => t(b) - t(a),
      high: (a, b) => (b.score ?? -1) - (a.score ?? -1) || t(b) - t(a),
      low: (a, b) => (a.score ?? 101) - (b.score ?? 101) || t(b) - t(a),
    };
    return [...list].sort(sorters[hSort] || sorters.recent);
  }, [inTab, role, company, hSort]);
  const switchTab = (id) => { setTab(id); setHRole(''); setHCompany(''); };

  if (view === 'history') {
    return (
      <IvShell hub={hub} {...shell}>
        <section className="ivx-head">
          <div>
            <button type="button" className="pu-link ivx-back" onClick={onBack}>← Interviews</button>
            <h1>Interview History</h1>
            <p>Review your past interviews and track how you’re improving.</p>
          </div>
        </section>
        <section className="pu-panel ivx-history">
          <div className="ivx-history-bar">
            <nav className="ivx-tabs" aria-label="Interview type">
              {[['all', 'All interviews'], ['ai', 'AI interviews'], ['written', 'Written interviews']].map(([id, l]) => (
                <button key={id} type="button" className={filter === id ? 'on' : ''} aria-pressed={filter === id} onClick={() => setFilter(id)}>{l}</button>
              ))}
            </nav>
            <div className="ivx-filters">
              <label className="kh-select"><span className="pu-sr">Role</span>
                <select value={roleF} onChange={(e) => setRoleF(e.target.value)} aria-label="Role">
                  <option value="">All roles</option>{roleNames.map((r) => <option key={r}>{r}</option>)}
                </select>
              </label>
              <label className="kh-select"><span className="pu-sr">Company</span>
                <select value={companyF} onChange={(e) => setCompanyF(e.target.value)} aria-label="Company">
                  <option value="">All companies</option>{companies.map((c) => <option key={c}>{c}</option>)}
                </select>
              </label>
              <label className="kh-select"><span className="pu-sr">Date</span>
                <select value={dateF} onChange={(e) => setDateF(e.target.value)} aria-label="Date">
                  <option value="any">Any time</option><option value="7">Last 7 days</option><option value="30">Last 30 days</option>
                </select>
              </label>
              <label className="kh-select"><span className="pu-sr">Score</span>
                <select value={scoreF} onChange={(e) => setScoreF(e.target.value)} aria-label="Score">
                  <option value="any">Any score</option><option value="high">80% and above</option><option value="mid">60–79%</option><option value="low">Below 60%</option>
                </select>
              </label>
            </div>
          </div>
          {rows.length ? <HistoryRows rows={rows} onView={onOpenReport} />
            : <p className="pu-empty">{history.length ? 'No interviews match these filters.' : 'No interviews yet — your finished interviews and their feedback will appear here.'}</p>}
          <p className="pu-meta ivx-note">History is kept in this browser.</p>
        </section>
      </IvShell>
    );
  }

  const emptyState = () => {
    if (!base.length) {
      return (
        <div className="ivh-empty">
          <b>No interviews yet</b>
          <span>Your interviews, scores and key takeaways appear here after your first one.</span>
          <div><button type="button" className="pu-btn pu-btn-sm pu-btn-primary" onClick={() => onSetup({ type: 'video' })}>Start AI Interview</button>
            <button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" onClick={() => onSetup({ type: 'written' })}>Start Written Interview</button></div>
        </div>
      );
    }
    if (!inTab.length) {
      const w = tab === 'written';
      return (
        <div className="ivh-empty">
          <b>{w ? 'No written interviews yet' : 'No AI interviews yet'}</b>
          <span>{w ? 'Practise a written interview to see your results here.' : 'Take an AI interview to see your results here.'}</span>
          <div><button type="button" className="pu-btn pu-btn-sm pu-btn-primary" onClick={() => onSetup({ type: w ? 'written' : 'video' })}>{w ? 'Start Written Interview' : 'Start AI Interview'}</button></div>
        </div>
      );
    }
    return (
      <div className="ivh-empty">
        <b>No {tab === 'all' ? '' : tab === 'ai' ? 'AI ' : 'written '}interviews{role ? ` for ${role}` : ''}{company ? ` at ${company}` : ''}</b>
        <div><button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" onClick={() => { setHRole(''); setHCompany(''); }}>Clear filters</button></div>
      </div>
    );
  };

  const band = (sc) => (sc == null ? null : sc >= 80 ? ['strong', 'Strong'] : sc >= 60 ? ['good', 'Good'] : ['weak', 'Needs work']);
  const recLevel = loadPrefs().experienceLevel || loadPrefs().interviewLevel || 'Mid-level';

  return (
    <IvShell hub={hub} className="ivh" {...shell}>
      <section className="ivh-intro">
        <div className="ivh-intro-text">
          <h1>Interviews</h1>
          <p>Practice under realistic interview conditions and improve your confidence.</p>
        </div>
        <ol className="ivh-loop" aria-label="How interviews fit your learning">
          {['Learn', 'Practice', 'Interview', 'Diagnose', 'Improve'].map((x) => <li key={x} className={x === 'Interview' ? 'on' : ''}>{x}</li>)}
        </ol>
      </section>

      <section className="ivh-hero" aria-label="Choose an interview">
        <article className="ivh-ai">
          <div className="ivh-ai-copy">
            <span className="ivh-tag">Recommended</span>
            <h2>AI Interview</h2>
            <p>Practise speaking under realistic interview conditions.</p>
            <ul className="ivh-list">
              <li><IconCheck size={13} />Video or voice interview</li>
              <li><IconCheck size={13} />Role-specific questions</li>
              <li><IconCheck size={13} />Personalised to your skills and weak areas</li>
              <li><IconCheck size={13} />Detailed feedback and next steps</li>
            </ul>
            <button type="button" className="pu-btn pu-btn-primary ivh-cta" onClick={() => onSetup({ type: 'video' })}>Start AI Interview <IconArrowRight size={15} /></button>
          </div>
          <div className="ivh-call" aria-hidden="true">
            <div className="ivh-call-top"><i />Recording<span>00:12:34</span></div>
            <div className="ivh-call-stage">
              <InterviewAvatar persona="professional" speaking size="md" label="AI interviewer" />
              <div className="ivh-call-bubble"><b>AI Interviewer</b>How would you evaluate a model before shipping it?</div>
              <div className="ivh-call-self"><span>You</span></div>
            </div>
            <div className="ivh-call-ctl"><span><IconMic size={13} /></span><span><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="13" height="12" rx="2.5" /><path d="M15 10.5 21 7v10l-6-3.5" /></svg></span><span className="end">End</span></div>
          </div>
        </article>

        <article className="ivh-written">
          <div className="ivh-written-copy">
            <h2>Written Interview</h2>
            <p>Practise structuring strong interview answers.</p>
            <ul className="ivh-list quiet">
              <li><IconCheck size={13} />Role-based questions</li>
              <li><IconCheck size={13} />Structured written responses</li>
              <li><IconCheck size={13} />Detailed feedback, by skill and topic</li>
              <li><IconCheck size={13} />Improve answer structure and clarity</li>
            </ul>
            <button type="button" className="pu-btn pu-btn-quiet ivh-cta" onClick={() => onSetup({ type: 'written' })}>Start Written Interview <IconArrowRight size={15} /></button>
          </div>
          <div className="ivh-paper" aria-hidden="true">
            <span className="ivh-paper-q">Q. Explain the difference between overfitting and underfitting.</span>
            <span className="ivh-paper-a"><i style={{ width: '92%' }} /><i style={{ width: '78%' }} /><i style={{ width: '55%' }} /></span>
            <span className="ivh-paper-tools"><b>B</b><em>I</em><span>≡</span><small>Structure ✓</small></span>
          </div>
        </article>
      </section>

      <section className="ivh-recent" aria-labelledby="ivh-recent-h">
        <div className="ivh-recent-head">
          <h2 id="ivh-recent-h">Recent Interviews</h2>
          {base.length > 0 && <button type="button" className="pu-link" onClick={onOpenHistory}>View all <IconArrowRight size={12} /></button>}
        </div>
        {base.length > 0 && (
          <div className="ivh-filters">
            <div className="ivh-tabs" role="tablist" aria-label="Interview type">
              {[['all', 'All'], ['ai', 'AI Interviews'], ['written', 'Written']].map(([id, l]) => (
                <button key={id} type="button" role="tab" aria-selected={tab === id} className={tab === id ? 'on' : ''} onClick={() => switchTab(id)}>
                  {l} <span>({counts[id]})</span>
                </button>
              ))}
            </div>
            <div className="ivh-selects">
              <label className="kh-select"><span className="pu-sr">Role</span>
                <select value={role} onChange={(e) => setHRole(e.target.value)} aria-label="Role" disabled={!tabRoles.length}>
                  <option value="">All roles</option>{tabRoles.map((r) => <option key={r}>{r}</option>)}
                </select>
              </label>
              <label className="kh-select"><span className="pu-sr">Company</span>
                <select value={company} onChange={(e) => setHCompany(e.target.value)} aria-label="Company" disabled={!tabCompanies.length}>
                  <option value="">All companies</option>{tabCompanies.map((c) => <option key={c}>{c}</option>)}
                </select>
              </label>
              <label className="kh-select"><span className="pu-sr">Sort</span>
                <select value={hSort} onChange={(e) => setHSort(e.target.value)} aria-label="Sort">
                  <option value="recent">Most recent</option><option value="high">Highest score</option><option value="low">Lowest score</option>
                </select>
              </label>
            </div>
          </div>
        )}
        {shown.length ? (
          <table className="ivh-table">
            <thead>
              <tr><th>Role / company</th><th>Type</th><th>Date</th><th><abbr title="Average of the question scores, each 0–100">Score</abbr></th><th>Key takeaway</th><th><span className="pu-sr">Action</span></th></tr>
            </thead>
            <tbody>
              {shown.slice(0, 5).map((e) => {
                const b = band(e.score);
                const written = historyFilterOf(e.kind) === 'written';
                return (
                  <tr key={e.id}>
                    <td>
                      <span className={`ivh-row-icon${written ? ' w' : ''}`} aria-hidden="true">{written ? <IconList size={14} /> : <IconInterview size={14} />}</span>
                      <span className="ivh-row-who"><b>{e.title}</b><small>{e.setup?.company || (e.kind === 'library' ? 'Interview library' : 'Role-based')}</small></span>
                    </td>
                    <td><span className={`ivh-type${written ? ' w' : ''}`}>{written ? 'Written' : e.kind === 'library' ? 'Library' : 'AI Interview'}</span></td>
                    <td className="ivh-muted">{ago(e.ts)}</td>
                    <td>{b ? <span className={`ivh-score ${b[0]}`}><b>{scoreText(e.score)}</b><small>{b[1]}</small></span> : <span className="ivh-muted">Not scored</span>}</td>
                    <td className="ivh-takeaway">{e.takeaway || takeawayOf(e.report)}</td>
                    <td className="ivh-act">{e.report ? <button type="button" className="pu-link" onClick={() => onOpenReport(e)} aria-label={`View feedback for ${e.title}`}>View <IconArrowRight size={12} /></button> : null}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : emptyState()}
        {shown.length > 5 && <p className="ivh-more"><button type="button" className="pu-link" onClick={onOpenHistory}>{shown.length - 5} more in your history <IconArrowRight size={12} /></button></p>}
      </section>

      <section className="ivh-rec" aria-live="polite">
        <div className="ivh-rec-start">
          <span className="ivh-rec-icon" aria-hidden="true"><IconTarget size={20} /></span>
          <div>
            <h2>Not sure where to start?</h2>
            <p>Get a personalised interview recommendation based on your goal, skill gaps, practice history and previous interviews.</p>
            <button type="button" className="pu-link" onClick={getRec} disabled={rec === 'loading'}>
              {rec === 'loading' ? 'Reading your progress…' : rec ? 'Refresh recommendation' : 'Get a recommendation'} <IconArrowRight size={12} />
            </button>
          </div>
        </div>
        <div className="ivh-rec-card">
          {rec && rec !== 'loading' ? (
            <>
              <div className="ivh-rec-top">
                <small>Recommended for you</small>
                <span className="ivh-rec-chips">
                  {rec.weakCount > 0 && <em className="warn">{rec.weakCount} weak area{rec.weakCount === 1 ? '' : 's'}</em>}
                  <em>~20 min</em>
                </span>
              </div>
              <b>{rec.role.title} · {recLevel}</b>
              <span className="ivh-rec-why">
                {rec.focus.length
                  ? <>Focus on {rec.focus.map((f, i) => <React.Fragment key={f.id}>{i > 0 && (i === rec.focus.length - 1 ? ' and ' : ', ')}<span title={f.reason}>{f.name}</span></React.Fragment>)} — because of {rec.because}.</>
                  : <>A core {rec.role.title} interview — because of {rec.because}.</>}
              </span>
              {rec.focus.length > 0 && <ul className="ivh-rec-reasons">{rec.focus.map((f) => <li key={f.id}><b>{f.name}</b> · {f.reason}</li>)}</ul>}
              <button type="button" className="pu-btn pu-btn-primary pu-btn-sm" onClick={() => onSetup({ ...rec.setup, level: recLevel })}>Start Recommended <IconArrowRight size={13} /></button>
            </>
          ) : rec === 'loading' ? <p className="ivh-muted">Reading your goal, practice history and previous interviews…</p>
            : <p className="ivh-muted">Your recommendation appears here — it uses your goal, weak areas and previous interviews.</p>}
        </div>
      </section>

      <p className="ivh-library"><button type="button" className="pu-link" onClick={onOpenLibrary}>Browse the interview library <IconArrowRight size={12} /></button></p>
    </IvShell>
  );
};

export default InterviewHome;
